import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { publicApi } from "@/lib/api";
import { ChemText } from "@/components/ChemText";
import { Clock, Send, ChevronLeft, ChevronRight, Save } from "lucide-react";
import LanguageSwitcher from "@/components/LanguageSwitcher";

export default function StudentQuiz() {
  const { code, studentId } = useParams();
  const nav = useNavigate();
  const [state, setState] = useState(null); // {status, session, questions, student}
  const [loadError, setLoadError] = useState(null);
  const [answers, setAnswers] = useState({}); // {question_id: answer}
  const [current, setCurrent] = useState(0);
  const [timeLeft, setTimeLeft] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitConfirm, setSubmitConfirm] = useState(false);
  const [savedTick, setSavedTick] = useState(0); // ticks to flash "Zapisano"
  const submittedRef = useRef(false);
  const tickRef = useRef(null);
  const pollRef = useRef(null);
  const pollTickRef = useRef(0);
  const answersRef = useRef(answers);
  const stateRef = useRef(state);
  const savingRef = useRef(false);
  const dirtyRef = useRef(false);
  const lastSavedRef = useRef("");

  useEffect(() => { answersRef.current = answers; }, [answers]);
  useEffect(() => { stateRef.current = state; }, [state]);

  const load = useCallback(async () => {
    try {
      const { data } = await publicApi.get(`/public/sessions/${code}/quiz/${studentId}`);
      setState(data);
      setLoadError(null);
      if (data.status === "active") {
        // Restore drafts on first load (only if we haven't started editing locally yet)
        if (data.saved_answers && Object.keys(data.saved_answers).length > 0) {
          setAnswers((prev) => {
            const merged = { ...data.saved_answers, ...prev };
            answersRef.current = merged;
            lastSavedRef.current = JSON.stringify(data.saved_answers);
            return merged;
          });
        }
        if (data.session.started_at && data.session.time_limit_minutes) {
          const start = new Date(data.session.started_at).getTime();
          const end = start + data.session.time_limit_minutes * 60 * 1000;
          const left = Math.max(0, Math.floor((end - Date.now()) / 1000));
          setTimeLeft(left);
        }
      }
    } catch (e) {
      // Only surface an error UI when we have no state yet — otherwise the
      // student is already in the quiz and background polling failures should
      // stay silent (network can flap for a second and recover).
      if (!stateRef.current) {
        setLoadError(e.response?.data?.detail || "Nie udało się załadować sesji");
      } else {
        // eslint-disable-next-line no-console
        console.warn("[StudentQuiz] background load failed:", e.message);
      }
    }
  }, [code, studentId]);

  // Autosave: debounced — fires whenever `answers` changes during active quiz
  const autosave = useCallback(async () => {
    if (submittedRef.current || savingRef.current) return;
    const cur = stateRef.current;
    if (!cur || cur.status !== "active") return;
    const ans = answersRef.current || {};
    const snapshot = JSON.stringify(ans);
    if (snapshot === lastSavedRef.current) return;
    savingRef.current = true;
    try {
      await publicApi.post(
        `/public/sessions/${code}/save-progress/${studentId}`,
        { answers: ans },
      );
      lastSavedRef.current = snapshot;
      dirtyRef.current = false;
      setSavedTick((t) => t + 1);
    } catch {
      // swallow — autosave is best-effort
    } finally {
      savingRef.current = false;
    }
  }, [code, studentId]);

  const submit = useCallback(async (auto = false) => {
    if (submittedRef.current) return;
    const cur = stateRef.current;
    const ans = answersRef.current;
    if (!cur) return;
    submittedRef.current = true;
    setSubmitting(true);
    try {
      const payload = cur.questions.map((q) => ({
        question_id: q.id, answer: ans[q.id] || "",
      }));
      await publicApi.post(`/public/sessions/${code}/submit/${studentId}`, payload);
      toast.success("Wysłano arkusz");
      nav(`/results/${code}/${studentId}`);
    } catch (e) {
      submittedRef.current = false;
      toast.error(e.response?.data?.detail || "Błąd wysyłania");
    } finally {
      setSubmitting(false);
    }
  }, [code, studentId, nav]);

  const handleSubmitClick = () => {
    if (submittedRef.current) return;
    const cur = stateRef.current;
    const ans = answersRef.current;
    if (!cur) return;
    const answeredCount = Object.values(ans).filter((a) => (a || "").trim()).length;
    if (answeredCount < cur.questions.length) {
      setSubmitConfirm(true);
    } else {
      submit(false);
    }
  };

  // Keep latest load() in a ref so the setInterval doesn't re-arm on each render
  const loadRef = useRef(load);
  useEffect(() => { loadRef.current = load; }, [load]);

  useEffect(() => {
    loadRef.current?.();
    pollRef.current = setInterval(() => {
      const cur = stateRef.current;
      // While in lobby → check every 3s if teacher started the quiz.
      // While active → check every 15s if teacher/cron ended it early.
      // Both branches are handled by the same load() call — server tells us
      // the current status.
      if (!cur) return loadRef.current?.();
      if (cur.status !== "active") return loadRef.current?.();
      // Active: throttle poll to 1-in-5 ticks (~15s at 3s cadence)
      pollTickRef.current = (pollTickRef.current || 0) + 1;
      if (pollTickRef.current % 5 === 0) loadRef.current?.();
    }, 3000);
    return () => clearInterval(pollRef.current);
  }, []);

  useEffect(() => {
    // Point 3+4 — when the session flips from active → ended (either the
    // teacher clicked "Zakończ" or the cron auto-ended after time limit),
    // route the student to the results page immediately. Their draft answers
    // have been auto-submitted server-side, so /result is the correct
    // destination for both submitted and previously-in-progress students.
    if (!state) return;
    if (state.status === "ended" && !submittedRef.current) {
      submittedRef.current = true; // stop autosave / prevent double redirect
      clearInterval(pollRef.current);
      toast.success("Nauczyciel zakończył kartkówkę — Twoje odpowiedzi zostały zapisane.");
      // small delay so the toast is visible before the route change
      setTimeout(() => nav(`/results/${code}/${studentId}`), 900);
    }
  }, [state, code, studentId, nav]);

  useEffect(() => {
    if (timeLeft === null) return;
    if (timeLeft <= 0) {
      if (!submittedRef.current && stateRef.current?.status === "active") {
        submit(true);
      }
      return;
    }
    tickRef.current = setTimeout(() => setTimeLeft((t) => (t !== null ? t - 1 : t)), 1000);
    return () => clearTimeout(tickRef.current);
  }, [timeLeft, submit]);

  // Debounced autosave: 1.2s after last change
  useEffect(() => {
    if (!state || state.status !== "active") return;
    dirtyRef.current = true;
    const t = setTimeout(() => { autosave(); }, 1200);
    return () => clearTimeout(t);
  }, [answers, state, autosave]);

  // Safety net: periodic autosave every 15s, plus on tab close
  useEffect(() => {
    if (!state || state.status !== "active") return;
    const interval = setInterval(() => { autosave(); }, 15000);
    const onUnload = () => {
      // Best-effort sync save via beacon when possible
      try {
        if (navigator.sendBeacon) {
          const url = `${publicApi.defaults.baseURL}/public/sessions/${code}/save-progress/${studentId}`;
          const blob = new Blob(
            [JSON.stringify({ answers: answersRef.current || {} })],
            { type: "application/json" },
          );
          navigator.sendBeacon(url, blob);
        }
      } catch { /* noop */ }
    };
    window.addEventListener("beforeunload", onUnload);
    return () => {
      clearInterval(interval);
      window.removeEventListener("beforeunload", onUnload);
    };
  }, [state, autosave, code, studentId]);

  const setAns = (qid, v) => setAnswers((prev) => ({ ...prev, [qid]: v }));

  if (!state) {
    if (loadError) {
      return (
        <div className="min-h-screen bg-[#FEE2E2] flex items-center justify-center px-6" data-testid="quiz-load-error">
          <div className="brut border-2 border-[#0F172A] shadow-[6px_6px_0_#0F172A] bg-white rounded-md p-8 max-w-md text-center">
            <div className="text-3xl mb-2" aria-hidden>📡</div>
            <h1 className="font-display font-black text-2xl">Nie mogę załadować sesji</h1>
            <p className="text-[#334155] mt-3 text-sm">{loadError}</p>
            <p className="text-[#64748B] mt-2 text-xs">
              Sprawdź kod sesji, połączenie z internetem albo wpisz kod jeszcze raz.
            </p>
            <div className="flex gap-2 justify-center mt-5">
              <button
                onClick={() => { setLoadError(null); load(); }}
                className="brut brut-hover bg-[#FEF08A] border-2 border-[#0F172A] rounded-md px-4 py-2 font-bold"
                data-testid="quiz-load-retry"
              >
                Spróbuj ponownie
              </button>
              <button
                onClick={() => nav("/join")}
                className="brut brut-hover bg-white border-2 border-[#0F172A] rounded-md px-4 py-2 font-bold"
                data-testid="quiz-load-back"
              >
                Wróć do dołączania
              </button>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="min-h-screen bg-[#E0F2FE] flex items-center justify-center">
        <div>Ładowanie...</div>
      </div>
    );
  }

  if (state.status === "lobby") {
    return (
      <div className="min-h-screen bg-[#FEF08A] flex items-center justify-center px-6">
        <div className="brut-lg bg-white rounded-md p-10 max-w-lg text-center" data-testid="waiting-room">
          <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#64748B]">Poczekalnia</div>
          <h1 className="font-display font-black text-3xl mt-2">Czekaj na nauczyciela</h1>
          <p className="text-[#334155] mt-3">Kartkówka rozpocznie się gdy nauczyciel ją uruchomi.</p>
          <div className="mt-6 inline-block brut bg-[#FEF08A] px-4 py-2 rounded-md">
            <div className="text-xs tracking-[0.2em] uppercase font-bold">Twoja sesja</div>
            <div className="font-display font-black text-2xl">{code}</div>
          </div>
        </div>
      </div>
    );
  }

  if (state.status === "ended") {
    return (
      <div className="min-h-screen bg-[#E0E7FF] flex items-center justify-center px-6">
        <div className="brut-lg bg-white rounded-md p-10 max-w-lg text-center" data-testid="quiz-ended-screen">
          <h1 className="font-display font-black text-3xl">Kartkówka zakończona</h1>
          <p className="text-[#334155] mt-3">
            Sesja została zamknięta przez nauczyciela. Twoje zapisane odpowiedzi
            zostały automatycznie oddane — sprawdź swój wynik poniżej.
          </p>
          <button
            onClick={() => nav(`/results/${code}/${studentId}`)}
            className="brut brut-hover bg-[#FEF08A] rounded-md px-5 py-3 font-black mt-6 inline-flex items-center gap-2"
            data-testid="quiz-ended-see-results-btn"
          >
            Zobacz swój wynik →
          </button>
        </div>
      </div>
    );
  }

  const q = state.questions[current];
  // Defensive: if `current` somehow points outside the questions array
  // (e.g., a stale index after a session reload), clamp back and re-render
  // instead of crashing with a white screen on `q.text`.
  if (!q) {
    const safeIdx = Math.max(0, Math.min(current, state.questions.length - 1));
    if (safeIdx !== current) {
      // Defer to next tick so React doesn't complain about updating during render
      setTimeout(() => setCurrent(safeIdx), 0);
    }
    return (
      <div className="min-h-screen bg-[#FEE2E2] flex items-center justify-center px-6">
        <div className="brut-lg bg-white rounded-md p-8 max-w-md text-center" data-testid="quiz-error-recover">
          <h1 className="font-display font-black text-2xl">Pytanie nie załadowało się</h1>
          <p className="text-[#334155] mt-3">Kliknij poniżej — Twoje odpowiedzi są zapisane.</p>
          <button
            onClick={() => window.location.reload()}
            className="brut brut-hover bg-[#FEF08A] rounded-md px-4 py-2 font-bold mt-5"
            data-testid="quiz-error-reload"
          >
            Odśwież stronę
          </button>
        </div>
      </div>
    );
  }
  const answered = state.questions.filter((qq) => (answers[qq.id] || "").trim()).length;
  const fmtTime = (s) => {
    if (s === null) return "--:--";
    const m = Math.floor(s / 60).toString().padStart(2, "0");
    const sec = (s % 60).toString().padStart(2, "0");
    return `${m}:${sec}`;
  };
  const timeWarn = timeLeft !== null && timeLeft < 60;

  return (
    <div className="min-h-screen bg-[#E0F2FE]">
      {/* Top bar */}
      <header className="border-b-2 border-[#0F172A] bg-white sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#64748B] truncate">{state.session.title}</div>
            <div className="font-display font-bold truncate">Kod: {code} · {state.student.name}</div>
          </div>
          <div className={`brut ${timeWarn ? "bg-[#EF4444] text-white" : "bg-[#FEF08A]"} px-3 py-2 rounded-md flex items-center gap-2 shrink-0`} data-testid="timer-display">
            <Clock className="w-4 h-4" strokeWidth={2.5} />
            <span className="font-display font-black text-lg">{fmtTime(timeLeft)}</span>
          </div>
          <div className="shrink-0"><LanguageSwitcher variant="flag-only" /></div>
        </div>
        {/* Autosave indicator */}
        <div className="max-w-4xl mx-auto px-4 -mt-1 mb-1 text-xs text-[#64748B] flex items-center gap-1 h-4">
          {savedTick > 0 && (
            <span key={savedTick} className="inline-flex items-center gap-1 animate-pulse" data-testid="autosave-indicator">
              <Save className="w-3 h-3" strokeWidth={2.5} /> Zapisano
            </span>
          )}
        </div>
        {/* Progress dots */}
        <div className="max-w-4xl mx-auto px-4 pb-3 flex gap-1 flex-wrap">
          {state.questions.map((qq, i) => {
            const ans = (answers[qq.id] || "").trim();
            const isCurrent = i === current;
            let dotBg = "bg-white";
            if (isCurrent) dotBg = "bg-[#0F172A] text-white";
            else if (ans) dotBg = "bg-[#D1FAE5]";
            return (
              <button
                key={qq.id}
                onClick={() => setCurrent(i)}
                className={`brut w-8 h-8 rounded-md flex items-center justify-center text-xs font-bold ${dotBg}`}
                data-testid={`q-nav-${i}`}
              >
                {i + 1}
              </button>
            );
          })}
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <div key={q.id} className="brut-lg bg-white rounded-md p-6 sm:p-8" data-testid={`question-card-${q.id}`}>
          <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#64748B]">
            Pytanie {current + 1} / {state.questions.length}
            {q.topic && ` · ${q.topic}`}
          </div>
          <h2 className="font-display font-bold text-2xl sm:text-3xl mt-2 leading-tight" data-testid="question-text">
            <ChemText text={q.text} />
          </h2>
          {q.image_url && (
            <div className="mt-4">
              <img
                src={q.image_url}
                alt="Ilustracja do pytania"
                className="brut rounded-md max-w-full max-h-80 object-contain bg-white"
                data-testid="question-image"
                loading="lazy"
              />
            </div>
          )}

          <div className="mt-6">
            {q.type === "mc" ? (
              <div className="grid gap-3">
                {(q.options || []).map((opt, idx) => {
                  const letter = String.fromCharCode(65 + idx);
                  const selected = answers[q.id] === letter;
                  return (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => setAns(q.id, letter)}
                      className={`brut brut-hover text-left rounded-md p-4 flex items-start gap-3 font-medium ${
                        selected ? "bg-[#10B981] text-white" : "bg-white"
                      }`}
                      data-testid={`option-${letter}`}
                    >
                      <span className={`brut min-w-[36px] h-9 flex items-center justify-center rounded-md font-bold ${
                        selected ? "bg-white text-[#0F172A]" : "bg-[#FEF08A]"
                      }`}>
                        {letter}
                      </span>
                      <span className="pt-1"><ChemText text={opt} /></span>
                    </button>
                  );
                })}
              </div>
            ) : (
              <Input
                value={answers[q.id] || ""}
                onChange={(e) => setAns(q.id, e.target.value)}
                className="brut-input border-2 border-[#0F172A] rounded-md h-14 text-lg"
                placeholder="Wpisz odpowiedź..."
                data-testid="open-answer-input"
              />
            )}
          </div>

          <div className="mt-8 flex items-center justify-between gap-3 flex-wrap">
            <Button
              onClick={() => setCurrent((c) => Math.max(0, c - 1))}
              disabled={current === 0}
              variant="outline"
              className="brut bg-white border-2 border-[#0F172A] font-bold rounded-md"
              data-testid="prev-btn"
            >
              <ChevronLeft className="w-4 h-4 mr-1" strokeWidth={2.5} />Poprzednie
            </Button>
            <div className="text-sm text-[#64748B]">
              Odpowiedziano: <b>{answered}/{state.questions.length}</b>
            </div>
            {current < state.questions.length - 1 ? (
              <Button
                onClick={() => setCurrent((c) => Math.min(state.questions.length - 1, c + 1))}
                className="brut brut-hover bg-[#0F172A] hover:bg-[#0F172A] text-white font-bold rounded-md"
                data-testid="next-btn"
              >
                Następne<ChevronRight className="w-4 h-4 ml-1" strokeWidth={2.5} />
              </Button>
            ) : (
              <Button
                onClick={handleSubmitClick}
                disabled={submitting}
                className="brut brut-hover bg-[#10B981] hover:bg-[#10B981] text-white font-bold rounded-md"
                data-testid="submit-quiz-btn"
              >
                <Send className="w-4 h-4 mr-2" strokeWidth={2.5} />Oddaj arkusz
              </Button>
            )}
          </div>
        </div>
      </main>

      <AlertDialog open={submitConfirm} onOpenChange={setSubmitConfirm}>
        <AlertDialogContent className="brut bg-white rounded-md border-2 border-[#0F172A]" data-testid="submit-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display font-black text-2xl">Oddać niepełny arkusz?</AlertDialogTitle>
            <AlertDialogDescription>
              {state && (
                <>Odpowiedziano na <b>{Object.values(answers).filter((a) => (a || "").trim()).length}/{state.questions.length}</b> pytań.
                  Brakujące pytania zostaną policzone jako błędne. Czy na pewno chcesz oddać teraz?</>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="brut bg-white border-2 border-[#0F172A] font-bold rounded-md"
              data-testid="submit-confirm-cancel-btn"
            >
              Wróć do pytań
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => submit(false)}
              className="brut brut-hover bg-[#10B981] hover:bg-[#10B981] text-white font-bold rounded-md"
              data-testid="submit-confirm-ok-btn"
            >
              Tak, oddaj
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
